import {useState, useEffect, useCallback, useRef} from "react";
import {Typography, Box, Paper} from "@mui/material";
import ResultsTabel from "../components/ResultsComponents/GeneralResults/ResultsTable";
import AvdelingButton from "../components/ResultsComponents/FilterButtons/DepartmentFilter";
import CourseButton from "../components/ResultsComponents/FilterButtons/CourseFilter";
import DateButton from "../components/ResultsComponents/FilterButtons/DateFilter";
import CourseOwnerButton from "../components/ResultsComponents/FilterButtons/CourseOwnerFilter";
import {
    GeneralResultAsUpperAdmin,
    GetResultAsDepartmentAdmin,
} from "../api/endpoints";
import apiClient from "../api/apiClient";
import Header from '../layout/Header'
import CenteredLoading from "../layout/CenteredLoading";
// axios is imported here only for CancelToken/isCancel utilities used to abort
// in-flight result fetches when the user changes filters. All HTTP calls go
// through apiClient (which IS an axios instance and shares the same CancelToken
// machinery). Do not call axios.get/post/put/delete from this file.
import axios from 'axios';

const ResultsPage = () => {
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [resultData, setResultData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRetrievingData, setIsRetrievingData] = useState(false);
    const cancelTokenSource = useRef(null);
    const latestRequestId = useRef(0); // Tracks the most recent request

    const role = localStorage.getItem("roles");
    const roleFromLocal = JSON.parse(role);
    const isDepartmentAdmin = useCallback(
        () => roleFromLocal.includes("org.admin.department"),
        [roleFromLocal]
    );

    // ! Subtract one month from the current date
    // const customStartDate = new Date("2023-01-01").toISOString();
    const customStartDate = new Date(null);

    // ! Add one month to the current date
    // const customEndDate = new Date(
    //   currentDate.getTime() + 30 * 24 * 60 * 60 * 1000
    // ).toISOString();
    const customEndDate = new Date(null);

    // console.log("general results", resultData);

    const handleSelectedDepartmentsChange = useCallback(
        (updatedSelectedDepartments) => {
            setSelectedDepartments(updatedSelectedDepartments);
        },
        []
    );

    const handleSelectedOwnerChange = useCallback((updatedSelectedOwner) => {
        setSelectedOwner(updatedSelectedOwner);
    }, []);

    const handleSelectedCourseChange = useCallback((updatedSelectedCourse) => {
        setSelectedCourse(updatedSelectedCourse);
    }, []);

    const handleDateChange = (start, end) => {
        if (!start || !end) {
            setStartDate(null);
            setEndDate(null);
            console.log("Date filter cleared");
            return;
        }
        
        const newStartDate = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
        const newEndDate = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));

        setStartDate(newStartDate);
        setEndDate(newEndDate);
        console.log(newStartDate, newEndDate); // These are correct and immediate
    };

    // ! Get All Results
    const params = {
        departmentNames: selectedDepartments,
        courseOwnerIds: selectedOwner,
        courseIds: selectedCourse,
        startDateTime: startDate,
        endDateTime: endDate,
    };

    async function getAllResults() {
        // if (isRetrievingData)
        //     return;

        // Cancel any ongoing request
        if (cancelTokenSource != null && cancelTokenSource.current) {
            cancelTokenSource.current.cancel("Request cancelled due to a new one.");
        }

        // Create new token
        cancelTokenSource.current = axios.CancelToken.source();
        const currentRequestId = ++latestRequestId.current;

        setLoading(true);
        // setIsRetrievingData(true)
        try {
            let endpoint = "";

            for (const role of roleFromLocal) {
                if (role === "org.admin.department") {
                    endpoint = GetResultAsDepartmentAdmin;
                    delete params.courseOwnerIds;
                    delete params.departmentNames;
                    break; // Break the loop once the endpoint is found
                } else if (
                    role === "org.admin.super" ||
                    role === "org.admin.assistant" ||
                    role === "org.admin.course"
                ) {
                    endpoint = GeneralResultAsUpperAdmin;
                    break; // Break the loop once the endpoint is found
                }
            }
            const response = await apiClient.post(
                endpoint,
                params,
                {
                    cancelToken: cancelTokenSource.current.token,
                }
            );

            // const response = await apiClient.post(endpoint, params, {});
            setResultData(response.data.data);
        } catch (err) {
            if (axios.isCancel(err)) {
            } else {
            }
        } finally {
            // Only reset loading if this is still the latest request
            if (currentRequestId === latestRequestId.current) {
                setLoading(false);
                cancelTokenSource.current = null;
            }
        }
    }


    useEffect(() => {
        // Make the request when all parameters have a valid value
        for (const role of roleFromLocal) {
            if (role === "org.admin.department") {
                if (selectedCourse.length > 0) {
                    getAllResults();
                }
            } else if (role !== "org.admin.department") {
                if (
                    selectedDepartments.length > 0 &&
                    selectedOwner.length > 0 &&
                    selectedCourse.length > 0
                ) {
                    getAllResults();
                }
            }
        }
    }, [selectedCourse, selectedDepartments, selectedOwner, startDate, endDate]);

    return (
        <Box width="100%">
            <Header header={'Resultater'} headerSize={'h6'} className={'resultater'}/>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {xs: "column", sm: "row"},
                    justifyContent: "flex-start",
                    alignItems: "center",
                    "& > *": {
                        mt: 2,
                    },
                }}
            >
                <Typography>Filtrér: </Typography>

                {!isDepartmentAdmin() && (
                    <>
                        <AvdelingButton
                            onSelectedDepartmentsChange={handleSelectedDepartmentsChange}
                        />
                        <CourseOwnerButton
                            onSelectedOwnerChange={handleSelectedOwnerChange}
                        />
                    </>
                )}
                <CourseButton onSelectedCourseChange={handleSelectedCourseChange}/>
                <DateButton onDateChange={handleDateChange}/>
            </Box>
            {loading && (
                <CenteredLoading tekst={'Laster inn…'}/>
            )}
            {!loading && (resultData === null || resultData?.length === 0) && (
                <Paper
                    sx={{
                        padding: 2,
                        marginTop: 6,
                        textAlign: "center",
                        width: "70%",
                        overflow: "auto",
                    }}
                >
                    <Typography variant="body1">Ingen kursresultater funnet</Typography>
                </Paper>
            )}
            {!loading && resultData?.length > 0 && (
                <ResultsTabel resultData={resultData}/>
            )}
        </Box>
    );
};

export default ResultsPage;
